import { ErrorHandler, Inject, Injectable } from '@angular/core';
import { OWNED_OBJECTS } from 'src/app/shared/helpers/owner.decorator';
import { TeamNames } from 'src/app/shared/enums/team-names.enum';
import { LoggerService } from './logger.service';
import { ENV, Environment } from 'src/environments/environment.provider';
import { WINDOW } from 'src/app/core/core.module';
import { AuthErrors } from '../enums/auth-errors.enum';
import { getStackTrace, stackTraceIncludesComponent } from '../helpers/stack-trace-helpers';

@Injectable()
export class NovaErrorHandler implements ErrorHandler {
    constructor(
        private readonly _loggerService: LoggerService,
        @Inject(ENV) private readonly _env: Environment,
        @Inject(WINDOW) private readonly _window: Window
    ) {}

    handleError(error: any): void {
        if (
            error?.error === AuthErrors.LoginRequired ||
            error?.error === AuthErrors.InvalidGrant ||
            error?.error === AuthErrors.MissingRefreshToken
        ) {
            return;
        }

        const chunkFailedMessage = /Loading chunk .*failed.*[.js\\)]/;
        const esBuildFailedMessage = /error loading dynamically imported module/;
        const fetchFailedMessage = /Failed to fetch dynamically imported module/;

        if (
            fetchFailedMessage.test(error.message) ||
            chunkFailedMessage.test(error.message) ||
            esBuildFailedMessage.test(error.message)
        ) {
            this._loggerService.error(this.buildErrorLogMessage('Chunk loading failed', error));

            if (
                this._window.confirm(
                    'A network error has occurred, your application experience might be degraded. Would you like to reload?'
                )
            ) {
                this._window.location.reload();
            }
        }

        const ownedComponents = Object.keys(OWNED_OBJECTS);
        const stackTrace = getStackTrace(error);
        // Get a list of teams that are responsible for the owned components in the stack trace.
        const applicableTeams = ownedComponents
            .filter((component) => stackTraceIncludesComponent(stackTrace, component))
            .map((component) => OWNED_OBJECTS[component]);

        let header = 'ERROR';

        if (applicableTeams?.length) {
            header = this.buildErrorHeader(applicableTeams);
        }

        // It is necessary to log the error object in the console, for two reasons:
        // 1. To maintain linking of source files in the console
        // 2. The logger service doesn't print to the console when running in prod mode
        console.error(header, error);

        if (this._env.production) {
            this._loggerService.error(this.buildErrorLogMessage(header, error), error);
        }
    }

    buildErrorLogMessage(header: string, error: any): string {
        return `${header}\n${error.message}\n${error.stack}`;
    }

    buildErrorHeader(teams: Array<TeamNames | TeamNames[]>): string {
        // When any of the components have multiple associated teams, flatten and distinctify the list
        teams = [...new Set(teams.flat())];
        return `ERROR - Please blame the following ${teams.length > 1 ? 'teams' : 'team'}: ${teams.join(',')} \n`;
    }
}
